import { useQueryClient } from '@tanstack/react-query';

import useAIJobChannel from 'hooks/useAIJobChannel';
import { Jobbable } from 'lib/aiJobHelpers';
import { clone } from 'lib/util';
import AIJob from 'models/AIJob';
import Attachment from 'models/Attachment';
import Admission from 'models/reviews/Admission';
import Authorization from 'models/reviews/Authorization';
import Referral from 'models/reviews/Referral';
import Summarization from 'models/Summarization';
import { admissionQueryKeys } from 'services/api/admissions';
import { authorizationQueryKeys, referralQueryKeys } from 'services/api/reviews';
import { summarizationQueryKeys } from 'services/api/summarizations';

function getJobbableConfig(jobbable: Jobbable) {
  if (jobbable instanceof Summarization) return { queryKeys: summarizationQueryKeys, klass: Summarization };
  if (jobbable instanceof Authorization) return { queryKeys: authorizationQueryKeys, klass: Authorization };
  if (jobbable instanceof Admission) return { queryKeys: admissionQueryKeys, klass: Admission };
  return { queryKeys: referralQueryKeys, klass: Referral };
}

// Maps the parent jobbable to the `attachable_model` the API's AttachmentPolicy::Scope
// expects. Required for extract-data jobs (ai_jobbable_type=Attachment) — without it,
// the server defaults to Activity scope and Summarization/Admission attachments fail
// authorization, leaving the channel in a silent retry loop.
function resolveAttachableModel(jobbable: Jobbable): string | undefined {
  if (jobbable instanceof Summarization) return 'Summarization';
  if (jobbable instanceof Admission) return 'Review::Admission';
  return undefined;
}

type JobbableAttachmentJobSubscriberProps = {
  attachment: Attachment;
  jobbable: Jobbable;
};

export default function JobbableAttachmentJobSubscriber({
  attachment,
  jobbable,
}: JobbableAttachmentJobSubscriberProps) {
  const queryClient = useQueryClient();
  const job = attachment.extractDataAIJob;

  const { queryKeys, klass } = getJobbableConfig(jobbable);
  useAIJobChannel({
    params: {
      ai_job_id: job?.isProcessing ? job.id : undefined,
      attachable_model: resolveAttachableModel(jobbable),
    },
    onMessageReceived: (data) => {
      const updatedJob = new AIJob(data);
      if (updatedJob.isProcessing) return;
      queryClient.setQueryData(queryKeys.show({ id: jobbable.id }), (oldData: Jobbable) => {
        if (!oldData?.attachments) return oldData;

        const updatedAttachments = oldData.attachments.map((att) => {
          if (att.id === data.aiJobbableId) att.extractDataAIJob = updatedJob;
          return att;
        });

        return clone(klass, oldData, { attachments: updatedAttachments });
      });
    },
  });

  return null;
}
